/**
 * Accessibility (ADA) enhancements
 * -----------------------------------------------------------------------------
 * Dedicated home for ADA fixes — never add these to theme.css / app.js /
 * utilities.js / custom.js (see CLAUDE.md).
 *
 * Navigation — items that own a sub menu:
 *
 *   1. The parent item is announced distinctly, e.g.
 *        collapsed -> "Womens, dropdown menu, press Enter to open"
 *        expanded  -> "Womens, dropdown menu, expanded, press Escape to close"
 *      The hint is rewritten whenever the menu opens or closes, so returning
 *      focus to the parent item never repeats a stale "press Enter to open".
 *
 *   2. aria-expanded / aria-controls are moved onto the focusable element. The
 *      theme ships them on the non-focusable wrapper <div>, where assistive
 *      tech (and the UserWay reader) never reaches them.
 *
 *   3. Escape closes an open menu and returns focus to its parent item.
 *
 *   4. Sub menu toggles that no keyboard could reach — second level items in
 *      the desktop dropdown, and the caret in the mobile menu — are exposed as
 *      real buttons, so their child links stop being a keyboard dead end.
 *
 * Covers the classic, centered, vertical and search-focus headers, the sticky
 * header clone, the mobile menu, and mega menus (which are injected after load).
 *
 * To localise, define window.PXUTheme.a11yMenuStrings = { collapsed: '…',
 * expanded: '…', expandedToggle: '…' } before this file runs.
 */
(function () {
  'use strict';

  var DEFAULT_STRINGS = {
    // Shown on a collapsed parent item.
    collapsed: 'dropdown menu, press Enter to open',
    // Shown on an expanded parent item that is also a link — a second Enter
    // follows the link, so Escape is what closes it.
    expanded: 'dropdown menu, expanded, press Escape to close',
    // Shown on an expanded parent item that is only a toggle.
    expandedToggle: 'dropdown menu, expanded, press Enter to close',
    // Shown on a split-menu toggle button.
    buttonCollapsed: 'press Enter to expand',
    buttonExpanded: 'press Escape to close'
  };

  var strings = {};
  var overrides =
    (window.PXUTheme && window.PXUTheme.a11yMenuStrings) || {};

  Object.keys(DEFAULT_STRINGS).forEach(function (key) {
    strings[key] = overrides[key] || DEFAULT_STRINGS[key];
  });

  var TOP_LEVEL_SELECTOR = [
    '.navbar-item.has-dropdown',
    '.navbar-item.has-mega-menu',
    '.vertical-header__first-level.has-dropdown',
    '.vertical-header__first-level.has-mega-menu'
  ].join(',');

  var SECOND_LEVEL_SELECTOR = '.navbar-dropdown .has-submenu';

  var MOBILE_SELECTOR = [
    '.mobile-menu__first-level[data-mobile-menu-has-toggle="true"]',
    '.mobile-menu__second-level'
  ].join(',');

  var DECORATED = 'data-a11y-menu-ready';
  var HINT_CLASS = 'a11y-menu-hint';

  var entries = [];
  var idCounter = 0;
  var syncQueued = false;
  var scanQueued = false;

  function uniqueId(prefix) {
    idCounter += 1;
    return prefix + idCounter;
  }

  function checkboxIn(wrapper) {
    return wrapper.querySelector(':scope > input[type="checkbox"]');
  }

  /* -------------------------------------------------------------------------
   * Desktop — top level items
   * ---------------------------------------------------------------------- */

  /* The theme wraps every navigation link in a <label>, so the trigger is
     either a direct child or one level down. */
  function linkIn(wrapper) {
    var child = wrapper.firstElementChild;

    while (child) {
      if (child.tagName === 'A') return child;
      if (child.tagName === 'LABEL') {
        var nested = child.querySelector('a');
        if (nested) return nested;
      }
      child = child.nextElementSibling;
    }

    return null;
  }

  // Split pattern: a dedicated toggle button beside the link, when the header renders one
  function toggleIn(wrapper) {
    return wrapper.querySelector(':scope > label > .header__submenu-toggle');
  }

  function topLevelTrigger(wrapper) {
    return toggleIn(wrapper) || linkIn(wrapper);
  }

  function topLevelPopup(wrapper) {
    return (
      wrapper.querySelector(':scope > .mega-menu__section') ||
      wrapper.querySelector(':scope > .navbar-dropdown')
    );
  }

  function topLevelExpanded(wrapper) {
    if (wrapper.classList.contains('is-closed')) {
      return false;
    }

    if (
      wrapper.classList.contains('is-opened') ||
      wrapper.classList.contains('show-dropdown')
    ) {
      return true;
    }

    var mega = wrapper.querySelector('.mega-menu__section');
    if (mega && mega.classList.contains('is-active')) return true;

    var megaMenu = wrapper.querySelector('.mega-menu');
    if (
      megaMenu &&
      (megaMenu.classList.contains('mega-menu--show') ||
        megaMenu.classList.contains('mega-menu--force-show'))
    ) {
      return true;
    }

    if (document.activeElement && wrapper.contains(document.activeElement)) {
      return true;
    }

    return false;
  }

  function openTopLevel(entry) {
    entry.wrapper.classList.remove('is-closed');
    entry.wrapper.classList.add('show-dropdown', 'is-opened');

    var mega = entry.wrapper.querySelector('.mega-menu__section');
    if (mega) mega.classList.add('is-active');

    var megaMenu = entry.wrapper.querySelector('.mega-menu');
    if (megaMenu) megaMenu.classList.add('mega-menu--show');
  }

  function closeTopLevel(entry) {
    entry.wrapper.classList.add('is-closed');
    entry.wrapper.classList.remove('show-dropdown', 'is-opened');

    var mega = entry.wrapper.querySelector('.mega-menu__section');
    if (mega) mega.classList.remove('is-active');

    var megaMenu = entry.wrapper.querySelector('.mega-menu');
    if (megaMenu) {
      megaMenu.classList.remove('mega-menu--show', 'mega-menu--force-show');
    }

    var dropdown = entry.wrapper.querySelector('.navbar-dropdown');
    if (dropdown) dropdown.classList.remove('show-nested-dropdown');

    var nested = entry.wrapper.querySelectorAll(
      '.has-submenu > input[type="checkbox"]'
    );
    for (var i = 0; i < nested.length; i++) nested[i].checked = false;
  }

  /* -------------------------------------------------------------------------
   * Desktop — second level items (vertical dropdown style)
   * ---------------------------------------------------------------------- */

  function secondLevelPopup(wrapper) {
    return wrapper.querySelector(':scope > .navbar-submenu');
  }

  function secondLevelExpanded(wrapper) {
    var checkbox = checkboxIn(wrapper);
    if (checkbox && checkbox.checked) return true;

    var dropdown = wrapper.closest('.navbar-dropdown');
    return !!(dropdown && dropdown.classList.contains('show-nested-dropdown'));
  }

  function openSecondLevel(entry) {
    var checkbox = checkboxIn(entry.wrapper);
    if (checkbox) checkbox.checked = true;
  }

  function closeSecondLevel(entry) {
    var checkbox = checkboxIn(entry.wrapper);
    if (checkbox) checkbox.checked = false;

    var dropdown = entry.wrapper.closest('.navbar-dropdown');
    if (dropdown) dropdown.classList.remove('show-nested-dropdown');
  }

  /* -------------------------------------------------------------------------
   * Mobile menu
   *
   * The caret (.close-dropdown) is the only thing that opens a sub menu, and a
   * bare <span> is neither focusable nor announced. The link beside it keeps
   * its own destination, so the caret becomes a separate toggle button.
   * ---------------------------------------------------------------------- */

  function mobileToggleIn(wrapper) {
    return wrapper.querySelector(
      ':scope > label > .close-dropdown, :scope > label > .close-sub-dropdown'
    );
  }

  function mobilePopup(wrapper) {
    return wrapper.querySelector(
      ':scope > .mobile-submenu__list, :scope > .mobile-sub-submenu__list'
    );
  }

  function mobileExpanded(wrapper) {
    var checkbox = checkboxIn(wrapper);
    return !!(checkbox && checkbox.checked);
  }

  function toggleMobile(entry) {
    /* The caret sits inside the <label>, so a click still reaches the checkbox
       the theme's CSS keys off. */
    entry.trigger.click();
  }

  function mobileTitle(wrapper) {
    var link = wrapper.querySelector(':scope > label > a');
    return link ? link.textContent : '';
  }

  /* -------------------------------------------------------------------------
   * Shared wiring
   * ---------------------------------------------------------------------- */

  function setHint(entry, expanded) {
    if (entry.plain) {
      var buttonLabel = (entry.title + ' submenu, ' + (expanded ? strings.buttonExpanded : strings.buttonCollapsed))
        .replace(/\s+/g, ' ').trim();
      if (entry.trigger.getAttribute('aria-label') !== buttonLabel) {
        entry.trigger.setAttribute('aria-label', buttonLabel);
      }
      return;
    }

    var phrase = strings.collapsed;

    if (expanded) {
      phrase = entry.toggles ? strings.expandedToggle : strings.expanded;
    }

    if (entry.hint) {
      var text = ', ' + phrase;
      if (entry.hint.textContent !== text) entry.hint.textContent = text;
      return;
    }

    var label = (entry.title + ', ' + phrase).replace(/\s+/g, ' ').trim();
    if (entry.trigger.getAttribute('aria-label') !== label) {
      entry.trigger.setAttribute('aria-label', label);
    }
  }

  function syncEntry(entry) {
    if (!entry.trigger.isConnected) return;

    /* Mega menus are cloned in after load and replace the plain dropdown,
       which the theme then hides — re-point aria-controls at the real popup. */
    var popup = entry.popupFor(entry.wrapper);
    if (popup && popup !== entry.popup) {
      entry.popup = popup;
      if (!popup.id) popup.id = uniqueId('a11y-submenu-');
      entry.trigger.setAttribute('aria-controls', popup.id);
    }

    var expanded = entry.isExpanded(entry.wrapper);
    entry.trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    setHint(entry, expanded);
  }

  function syncAll() {
    syncQueued = false;

    for (var i = entries.length - 1; i >= 0; i--) {
      if (entries[i].trigger.isConnected) {
        syncEntry(entries[i]);
      } else {
        entries.splice(i, 1);
      }
    }
  }

  function scheduleSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(syncAll);
  }

  function decorate(wrapper, options) {
    if (wrapper.hasAttribute(DECORATED)) return;

    var trigger = options.trigger(wrapper);
    if (!trigger) return;

    var popup = options.popup(wrapper);
    if (!popup) return;

    wrapper.setAttribute(DECORATED, 'true');

    /* The theme puts these on the wrapper <div>, which has no role and is not
       focusable — assistive tech never announces them there. */
    wrapper.removeAttribute('aria-haspopup');
    wrapper.removeAttribute('aria-expanded');

    var plain = trigger.classList.contains('header__submenu-toggle');

    if (!popup.id) popup.id = uniqueId('a11y-submenu-');
    if (!plain) trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-controls', popup.id);
    trigger.setAttribute('aria-expanded', 'false');

    /* Anything that is not a real link needs a role and a tab stop of its own
       before a keyboard can get to it. */
    if (!plain && !trigger.hasAttribute('href')) {
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
    }

    var entry = {
      wrapper: wrapper,
      trigger: trigger,
      popup: popup,
      popupFor: options.popup,
      isExpanded: options.isExpanded,
      open: options.open,
      close: options.close,
      toggles: options.toggles === true,
      plain: plain,
      hint: null,
      title: ''
    };

    if (plain) {
      var link = linkIn(wrapper);
      entry.title = link ? link.textContent : '';
    } else if (options.title) {
      /* Empty trigger — the name has to carry the item title itself. */
      entry.title = options.title(wrapper);
    } else {
      /* The trigger already reads out its own text, so append the hint to it. */
      entry.hint = document.createElement('span');
      entry.hint.className = HINT_CLASS;
      trigger.appendChild(entry.hint);
    }

    entries.push(entry);

    new MutationObserver(scheduleSync).observe(wrapper, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    var checkbox = checkboxIn(wrapper);
    if (checkbox) checkbox.addEventListener('change', scheduleSync);

    wrapper.addEventListener('focusout', function (e) {
      if (!wrapper.contains(e.relatedTarget)) {
        wrapper.classList.remove('is-closed');
      }
    });

    wrapper.addEventListener('mouseenter', function () {
      wrapper.classList.remove('is-closed');
    });

    syncEntry(entry);
  }

  function isSplitLink(element) {
    var item = element.closest && element.closest('.has-submenu-toggle');
    return !!item && linkIn(item) === element;
  }

  function entryForTrigger(element) {
    for (var i = 0; i < entries.length; i++) {
      if (
        entries[i].trigger === element ||
        entries[i].trigger.contains(element)
      ) {
        return entries[i];
      }
    }
    return null;
  }

  /* First Enter opens the menu, a second Enter follows the link — the theme's
     own behaviour, but driven by the real state so it stays correct after the
     menu has been closed with Escape.

     Bound to document in the capture phase: the theme binds its handler to the
     link itself, and it must not see the event first. */
  function handleActivation(event) {
    var isEnter = event.key === 'Enter' || event.keyCode === 13;
    var isSpace = event.key === ' ' || event.keyCode === 32;
    if (!isEnter && !isSpace) return;

    var entry = entryForTrigger(event.target);

    // Split items: the button toggles via its native click, the link just navigates; keep the theme's first-Enter handlers out
    if (entry && entry.plain) {
      event.stopPropagation();
      return;
    }
    if (!entry) {
      if (isEnter && isSplitLink(event.target)) event.stopPropagation();
      return;
    }
    if (isSpace && entry.trigger.hasAttribute('href')) return;

    var expanded = entry.isExpanded(entry.wrapper);
    if (expanded && !entry.toggles) return;

    event.preventDefault();

    if (expanded) {
      entry.close(entry);
    } else {
      entry.open(entry);
    }

    scheduleSync();
  }

  function handleEscape(event) {
    if (event.key !== 'Escape' && event.keyCode !== 27) return;

    var active = document.activeElement;
    if (!active) return;

    var searchOverlay = document.querySelector('[data-search-type="overlay"].is-opened');
    if (searchOverlay) {
      event.preventDefault();
      if (window.PXUTheme && window.PXUTheme.jsHeader && window.PXUTheme.jsHeader.hideSearch) {
        window.PXUTheme.jsHeader.hideSearch();
      }
      var searchTrigger = document.querySelector('[data-show-search-trigger]');
      if (searchTrigger) searchTrigger.focus();
      return;
    }

    var cartDrawer = document.querySelector('#theme-ajax-cart.is-drawer.is-visible');
    if (cartDrawer) {
      event.preventDefault();
      if (window.PXUTheme && window.PXUTheme.jsAjaxCart && window.PXUTheme.jsAjaxCart.hideDrawer) {
        window.PXUTheme.jsAjaxCart.hideDrawer();
      }
      var cartTrigger = document.querySelector('[data-ajax-cart-trigger]');
      if (cartTrigger) cartTrigger.focus();
      return;
    }

    /* Close the innermost open menu first, then its parent. */
    var open = null;
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry.wrapper.contains(active)) continue;
      if (!entry.isExpanded(entry.wrapper)) continue;
      if (!open || open.wrapper.contains(entry.wrapper)) open = entry;
    }

    if (!open) return;

    event.preventDefault();
    open.close(open);
    open.trigger.focus();
    scheduleSync();
  }

  function decorateAll(selector, options) {
    var wrappers = document.querySelectorAll(selector);

    for (var i = 0; i < wrappers.length; i++) {
      if (options.requiresCheckbox && !checkboxIn(wrappers[i])) continue;
      decorate(wrappers[i], options);
    }
  }

  function scan() {
    scanQueued = false;

    decorateAll(TOP_LEVEL_SELECTOR, {
      trigger: topLevelTrigger,
      popup: topLevelPopup,
      isExpanded: topLevelExpanded,
      open: openTopLevel,
      close: closeTopLevel
    });

    /* The horizontal dropdown style also uses .has-submenu, but its lists are
       always visible and have no checkbox to toggle. */
    decorateAll(SECOND_LEVEL_SELECTOR, {
      requiresCheckbox: true,
      trigger: linkIn,
      popup: secondLevelPopup,
      isExpanded: secondLevelExpanded,
      open: openSecondLevel,
      close: closeSecondLevel
    });

    decorateAll(MOBILE_SELECTOR, {
      requiresCheckbox: true,
      trigger: mobileToggleIn,
      title: mobileTitle,
      popup: mobilePopup,
      isExpanded: mobileExpanded,
      open: toggleMobile,
      close: toggleMobile,
      toggles: true
    });

    syncAll();
    ensureSearchButtonTriggers();
    ensureCartButtonTriggers();
    enhanceSearchAccessibility();
    enhanceAnnouncementBar();
    enhanceCartAccessibility();
    enhanceAddToCartButtons();
    enhanceSortDropdowns();
    enhanceProductSwatches();
    enhanceBannerImages();
    enhanceBannerHeadings();
    enhanceNewsletterText();
    enhancePhoneNumbers();
    focusFooterLabel('Email:');
    focusFooterLabel('Press Relations:');
    enhanceFooterText();
    enhancePrivacyChoicesText();
    enhancePrivacyMessages();
    enhanceRichText();
    enhancePageContentText();
    enhanceProductPrices();
    enhanceProductCardTitles();
    enhanceGridSliders();
  }

  function formatCartLabel(count) {
    var num = parseInt(count, 10);
    if (isNaN(num)) num = 0;
    var unit = num === 1 ? 'item' : 'items';
    return 'Cart, ' + num + ' ' + unit;
  }

  // Ensure cart triggers are real buttons for screen readers and keyboard users
  function ensureCartButtonTriggers() {
    var cartTriggers = document.querySelectorAll(
      '.header-cart > a.header__link, [data-ajax-cart-trigger] > a.header__link'
    );
    for (var i = 0; i < cartTriggers.length; i++) {
      var a = cartTriggers[i];
      var btn = document.createElement('button');
      btn.type = 'button';
      for (var j = 0; j < a.attributes.length; j++) {
        var attr = a.attributes[j];
        if (attr.name !== 'href') {
          btn.setAttribute(attr.name, attr.value);
        }
      }
      var countEl = a.querySelector('[data-bind="itemCount"]');
      var count = countEl ? countEl.textContent.trim() : '0';
      btn.setAttribute('aria-label', formatCartLabel(count));
      if (!btn.hasAttribute('aria-haspopup')) {
        btn.setAttribute('aria-haspopup', 'dialog');
      }
      while (a.firstChild) {
        btn.appendChild(a.firstChild);
      }
      a.parentNode.replaceChild(btn, a);
    }

    // Keep aria-label updated on existing cart buttons when count changes
    var cartButtons = document.querySelectorAll(
      '.header-cart > button.header__link, [data-ajax-cart-trigger] > button.header__link'
    );
    for (var k = 0; k < cartButtons.length; k++) {
      var cBtn = cartButtons[k];
      var cCountEl = cBtn.querySelector('[data-bind="itemCount"]');
      var cCount = cCountEl ? cCountEl.textContent.trim() : '0';
      var cLabel = formatCartLabel(cCount);
      if (cBtn.getAttribute('aria-label') !== cLabel) {
        cBtn.setAttribute('aria-label', cLabel);
      }
    }
  }

  // Cart drawer accessibility (WCAG 1.4.10, 2.1.1, 4.1.2)
  function enhanceCartAccessibility() {
    var cartDrawer = document.querySelector('#theme-ajax-cart.is-drawer');
    if (cartDrawer) {
      if (!cartDrawer.getAttribute('role')) {
        cartDrawer.setAttribute('role', 'dialog');
      }
      if (!cartDrawer.getAttribute('aria-label')) {
        cartDrawer.setAttribute('aria-label', 'Shopping Cart');
      }
    }

    var closeButtons = document.querySelectorAll('.ajax-cart__close-icon, [data-ajax-cart-close]');
    for (var i = 0; i < closeButtons.length; i++) {
      var btn = closeButtons[i];
      if (!btn.classList.contains('ajax-cart__overlay')) {
        if (!btn.getAttribute('role')) {
          btn.setAttribute('role', 'button');
        }
        if (!btn.hasAttribute('tabindex')) {
          btn.setAttribute('tabindex', '0');
        }
        if (!btn.getAttribute('aria-label')) {
          btn.setAttribute('aria-label', 'Close cart');
        }
      }
    }
  }

  // Ensure all search triggers are real buttons for screen readers and keyboard users
  function ensureSearchButtonTriggers() {
    var searchLinks = document.querySelectorAll('a[data-show-search-trigger]');
    for (var i = 0; i < searchLinks.length; i++) {
      var a = searchLinks[i];
      var btn = document.createElement('button');
      btn.type = 'button';
      for (var j = 0; j < a.attributes.length; j++) {
        var attr = a.attributes[j];
        if (attr.name !== 'href') {
          btn.setAttribute(attr.name, attr.value);
        }
      }
      btn.setAttribute('aria-haspopup', 'dialog');
      if (!btn.getAttribute('aria-label')) {
        btn.setAttribute('aria-label', 'Search');
      }
      while (a.firstChild) {
        btn.appendChild(a.firstChild);
      }
      a.parentNode.replaceChild(btn, a);
    }
  }

  // Search inputs accessibility (WCAG 2.1.1, 4.1.2)
  function enhanceSearchAccessibility() {
    var searchInputs = document.querySelectorAll('form[action*="/search"] input[name="q"], [data-q]');
    for (var i = 0; i < searchInputs.length; i++) {
      var input = searchInputs[i];
      if (!input.id) {
        input.id = 'search-input-' + (i + 1);
      }
      var form = input.closest('form');
      if (form) {
        if (!form.getAttribute('role')) {
          form.setAttribute('role', 'search');
        }
        var label = form.querySelector('label[for="q"]');
        if (label) {
          label.setAttribute('for', input.id);
        }
      }
      if (!input.getAttribute('aria-label')) {
        var placeholder = input.getAttribute('placeholder');
        if (placeholder) {
          input.setAttribute('aria-label', placeholder);
        }
      }
    }

    // Search close buttons (Fancybox and overlay)
    var searchCloseButtons = document.querySelectorAll(
      '.search__lightbox [data-fancybox-close], .fancybox-close-small, [data-fancybox-close], .search-overlay__close, [data-search-close]'
    );
    for (var j = 0; j < searchCloseButtons.length; j++) {
      var cBtn = searchCloseButtons[j];
      if (cBtn.getAttribute('aria-label') !== 'Close') {
        cBtn.setAttribute('aria-label', 'Close');
      }
      cBtn.setAttribute('title', 'Close');
      if (cBtn.tagName !== 'BUTTON' && !cBtn.getAttribute('role')) {
        cBtn.setAttribute('role', 'button');
        cBtn.setAttribute('tabindex', '0');
      }
    }
  }

  // Announcement bar keyboard focusability and screen reader name (WCAG 2.1.1, 4.1.2)
  function enhanceAnnouncementBar() {
    var wraps = document.querySelectorAll(
      '#announcement-bar .is-hidden-mobile-only, #announcement-bar .is-hidden-desktop-only'
    );

    for (var i = 0; i < wraps.length; i++) {
      var targets = getInnermostTextElements(wraps[i]);

      for (var j = 0; j < targets.length; j++) {
        var target = targets[j];
        var text = (target.textContent || '').replace(/\s+/g, ' ').trim();

        if (target.getAttribute('tabindex') !== '0') {
          target.setAttribute('tabindex', '0');
        }

        /* A focused <p> has no accessible name (paragraph role cannot be named from
           content), so screen readers announce nothing. Give it a nameable role. */
        if (target.getAttribute('role') !== 'group') {
          target.setAttribute('role', 'group');
        }
        if (target.getAttribute('aria-label') !== text) {
          target.setAttribute('aria-label', text);
        }
      }
    }
  }

  function getInnermostTextElements(el) {
    var results = [];
    var children = el.children;

    var hasOwnText = false;
    for (var n = 0; n < el.childNodes.length; n++) {
      var node = el.childNodes[n];
      if (node.nodeType === 3 && node.nodeValue.trim()) {
        hasOwnText = true;
        break;
      }
    }

    if (!children.length || hasOwnText) {
      if ((el.textContent || '').trim()) results.push(el);
      return results;
    }

    for (var c = 0; c < children.length; c++) {
      results = results.concat(getInnermostTextElements(children[c]));
    }
    return results;
  }

  // Main banner / hero images keyboard focusability (WCAG 2.1.1)
  function enhanceBannerImages() {
    var bannerImgs = document.querySelectorAll(
      '.image-element__wrap img, .banner img'
    );
    for (var i = 0; i < bannerImgs.length; i++) {
      var img = bannerImgs[i];
      if (img.getAttribute('tabindex') !== '0') {
        img.setAttribute('tabindex', '0');
      }
      if (img.getAttribute('role') === 'presentation' || img.getAttribute('role') === 'none') {
        img.removeAttribute('role');
      }
    }
  }

  // Banner headings & text elements keyboard focusability (WCAG 2.1.1)
  function enhanceBannerHeadings() {
    // Remove tabindex from wrapper spans containing text blocks
    var wrapperSpans = document.querySelectorAll(
      '.image-with-text-overlay__subheading span, .banner__subheading span, .image-with-text-overlay__preheading span'
    );
    for (var j = 0; j < wrapperSpans.length; j++) {
      wrapperSpans[j].removeAttribute('tabindex');
    }

    // Set tabindex="0" directly on the <p> elements
    var paragraphs = document.querySelectorAll(
      '.image-with-text-overlay__subheading p, .banner__subheading p, .image-with-text-overlay__preheading p'
    );
    for (var k = 0; k < paragraphs.length; k++) {
      if (paragraphs[k].getAttribute('tabindex') !== '0') {
        paragraphs[k].setAttribute('tabindex', '0');
      }
    }

    // Set tabindex="0" on heading spans
    var headingSpans = document.querySelectorAll(
      '.image-with-text-overlay__heading span, .banner__heading span'
    );
    for (var i = 0; i < headingSpans.length; i++) {
      if (headingSpans[i].getAttribute('tabindex') !== '0') {
        headingSpans[i].setAttribute('tabindex', '0');
      }
    }
  }

  // Newsletter heading/subheading and footer text block keyboard focusability (WCAG 2.1.1)
  function enhanceNewsletterText() {
    var textEls = document.querySelectorAll(
      '.newsletter__heading, .newsletter__subheading p, .footer__block.block__text p:not(.footer__heading)'
    );
    for (var i = 0; i < textEls.length; i++) {
      if (textEls[i].querySelector('[data-a11y-phone], a')) continue;
      if (textEls[i].textContent.trim() && textEls[i].getAttribute('tabindex') !== '0') {
        textEls[i].setAttribute('tabindex', '0');
      }
    }
  }

  // Dotted phone numbers read as decimals; focus the number and announce it hyphenated (WCAG 1.3.1)
  function enhancePhoneNumbers() {
    var containers = document.querySelectorAll('.footer__block.block__text');
    var pattern = /\b(?:1\.)?\d{3}\.\d{3}\.\d{4}\b/g;

    for (var i = 0; i < containers.length; i++) {
      var walker = document.createTreeWalker(containers[i], NodeFilter.SHOW_TEXT, null, false);
      var textNodes = [];
      while (walker.nextNode()) {
        var node = walker.currentNode;
        pattern.lastIndex = 0;
        if (!node.parentNode.closest('[data-a11y-phone]') && pattern.test(node.nodeValue)) {
          textNodes.push(node);
        }
      }

      for (var j = 0; j < textNodes.length; j++) {
        var textNode = textNodes[j];
        var text = textNode.nodeValue;
        var para = textNode.parentNode.closest('p');
        var prefix = para ? para.textContent.split(text)[0].replace(/\s+/g, ' ').trim() : '';
        var fragment = document.createDocumentFragment();
        var lastIndex = 0;
        var match;
        pattern.lastIndex = 0;

        while ((match = pattern.exec(text)) !== null) {
          var before = text.slice(lastIndex, match.index);
          var leading = before.match(/^\s*/)[0];
          fragment.appendChild(document.createTextNode(leading));

          var label = (prefix + ' ' + before).replace(/\s+/g, ' ').trim();
          var number = document.createElement('span');
          number.setAttribute('data-a11y-phone', '');
          number.setAttribute('tabindex', '0');
          number.setAttribute('role', 'img');
          number.setAttribute('aria-label', (label ? label + ' ' : '') + match[0].replace(/\./g, '-'));
          number.textContent = before.slice(leading.length) + match[0];

          fragment.appendChild(number);
          lastIndex = match.index + match[0].length;
        }

        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        textNode.parentNode.replaceChild(fragment, textNode);

        if (para) para.removeAttribute('tabindex');
      }
    }
  }

  // Footer contact label (e.g. "Email:") keyboard focusability (WCAG 2.1.1)
  function focusFooterLabel(text) {
    var paragraphs = document.querySelectorAll('.footer__block.block__text p');
    for (var i = 0; i < paragraphs.length; i++) {
      var node = paragraphs[i].firstChild;
      if (node && node.nodeType === 3 && node.nodeValue.trim() === text) {
        var label = document.createElement('span');
        label.setAttribute('tabindex', '0');
        label.textContent = node.nodeValue;
        paragraphs[i].replaceChild(label, node);
      }
    }
  }

  // Text keyboard focusability on the innermost text: loose text beside nested elements is wrapped in a focusable span (WCAG 2.1.1)
  var NATIVELY_FOCUSABLE = 'a[href], button, input, select, textarea, summary, iframe, [tabindex], [contenteditable="true"]';

  function makeTextFocusable(el) {
    if (el.matches(NATIVELY_FOCUSABLE) || el.getAttribute('aria-hidden') === 'true') return;

    if (!el.children.length) {
      if (el.textContent.trim()) el.setAttribute('tabindex', '0');
      return;
    }

    var nodes = Array.prototype.slice.call(el.childNodes);
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];

      if (node.nodeType === 1) {
        makeTextFocusable(node);
      } else if (node.nodeType === 3 && /[A-Za-z0-9]/.test(node.nodeValue)) {
        var span = document.createElement('span');
        span.setAttribute('tabindex', '0');
        span.setAttribute('data-a11y-text', '');
        span.textContent = node.nodeValue.trim();

        var fragment = document.createDocumentFragment();
        var leading = node.nodeValue.match(/^\s*/)[0];
        var trailing = node.nodeValue.match(/\s*$/)[0];
        if (leading) fragment.appendChild(document.createTextNode(leading));
        fragment.appendChild(span);
        if (trailing) fragment.appendChild(document.createTextNode(trailing));
        el.replaceChild(fragment, node);
      }
    }
  }

  // Page content text keyboard focusability; authored tabindex on a wrapper moves to the innermost text instead
  function enhancePageContentText() {
    var wrappers = document.querySelectorAll('.page__content ul[tabindex="0"], .page__content ol[tabindex="0"]');
    for (var w = 0; w < wrappers.length; w++) wrappers[w].removeAttribute('tabindex');

    var els = document.querySelectorAll(
      '.page__content p, .page__content li, .page__content h1, .page__content h2, .page__content h3, .page__content h4, .page__content h5, .page__content h6'
    );
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.closest('form, label, button') || el.getAttribute('tabindex') === '-1') continue;
      if (el.querySelector('p, ul, ol, li, div, table, h1, h2, h3, h4, h5, h6')) continue;
      if (!el.textContent.trim()) continue;

      if (el.getAttribute('tabindex') !== '0') {
        makeTextFocusable(el);
        continue;
      }

      var hasContentElements = false;
      for (var c = 0; c < el.children.length; c++) {
        if (el.children[c].tagName !== 'BR') { hasContentElements = true; break; }
      }
      if (!hasContentElements) continue;

      el.removeAttribute('tabindex');
      makeTextFocusable(el);
    }

    // Spacer-only child spans (e.g. <span>&nbsp;</span>) split a focusable span's text; flatten them into plain text
    var spans = document.querySelectorAll('.page__content span[tabindex="0"]');
    for (var s = 0; s < spans.length; s++) {
      var kids = spans[s].children;
      for (var k = kids.length - 1; k >= 0; k--) {
        if (kids[k].tagName === 'SPAN' && !kids[k].children.length && !kids[k].textContent.trim()) {
          spans[s].replaceChild(document.createTextNode(kids[k].textContent), kids[k]);
        }
      }
      spans[s].normalize();
    }
  }

  function enhanceRichText() {
    var els = document.querySelectorAll(
      '.rich-text__content .rich-text__heading, .rich-text__content .rich-text__text p'
    );
    for (var i = 0; i < els.length; i++) makeTextFocusable(els[i]);
  }

  function enhancePrivacyMessages() {
    var msgs = document.querySelectorAll('#pc--opt-out-success, #pc--opt-out-message, #pc--opt-out-error');
    for (var i = 0; i < msgs.length; i++) {
      if (msgs[i].hasAttribute('tabindex')) {
        for (var c = 0; c < msgs[i].children.length; c++) makeTextFocusable(msgs[i].children[c]);
      } else {
        makeTextFocusable(msgs[i]);
      }
    }
  }

  function enhancePrivacyChoicesText() {
    var form = document.getElementById('pc--optOutFormContainer');
    var content = form && form.closest('.page__content');
    if (!content) return;

    var els = content.querySelectorAll('p, h2, h3, h4, h5, h6, li');
    for (var i = 0; i < els.length; i++) {
      if (els[i].closest('label, button')) continue;
      makeTextFocusable(els[i]);
    }
  }

  function enhanceFooterText() {
    var els = document.querySelectorAll('.footer__credits p');
    for (var i = 0; i < els.length; i++) makeTextFocusable(els[i]);
  }

  // Grid slider carousel: every slide stays readable and focusable; focus scrolls its slide into view (WCAG 1.3.1, 2.4.11, 4.1.2)
  function syncSlideVisibility(flkty) {
    for (var i = 0; i < flkty.cells.length; i++) {
      flkty.cells[i].element.removeAttribute('aria-hidden');
    }
  }

  function isCellInView(slider, cell) {
    var viewport = slider.querySelector('.flickity-viewport');
    if (!viewport) return true;
    var view = viewport.getBoundingClientRect();
    var rect = cell.getBoundingClientRect();
    return rect.left >= view.left - 1 && rect.right <= view.right + 1;
  }

  function enhanceGridSliders() {
    var sliders = document.querySelectorAll('[data-grid-section-slider].flickity-enabled');

    for (var i = 0; i < sliders.length; i++) {
      var slider = sliders[i];
      var flkty = (window.jQuery && window.jQuery(slider).data('flickity')) ||
        (window.Flickity && window.Flickity.data && window.Flickity.data(slider));
      if (!flkty || slider.hasAttribute('data-a11y-carousel')) continue;
      slider.setAttribute('data-a11y-carousel', '');

      slider.setAttribute('role', 'group');
      slider.setAttribute('aria-roledescription', 'carousel');
      slider.setAttribute('aria-label', 'Collections carousel');
      slider.removeAttribute('tabindex');

      var total = flkty.cells.length;
      for (var c = 0; c < total; c++) {
        var cell = flkty.cells[c].element;
        cell.setAttribute('role', 'group');
        cell.setAttribute('aria-roledescription', 'slide');
        cell.setAttribute('aria-label', (c + 1) + ' of ' + total);

        var texts = cell.querySelectorAll('.grid-item__heading, .grid-item__text');
        for (var t = 0; t < texts.length; t++) makeTextFocusable(texts[t]);
      }

      slider.addEventListener('focusin', function (e) {
        var s = e.currentTarget;
        var f = (window.jQuery && window.jQuery(s).data('flickity')) ||
          (window.Flickity && window.Flickity.data && window.Flickity.data(s));
        if (!f) return;
        var vp = s.querySelector('.flickity-viewport');
        if (vp && vp.scrollLeft) vp.scrollLeft = 0;
        for (var n = 0; n < f.cells.length; n++) {
          var el = f.cells[n].element;
          if (el.contains(e.target) && !isCellInView(s, el)) {
            f.select(n);
            break;
          }
        }
      });

      (function (s, f) {
        var sync = function () {
          s.removeAttribute('tabindex');
          syncSlideVisibility(f);
        };
        f.on('select', function () { window.setTimeout(sync, 0); });
        f.on('settle', sync);
        window.addEventListener('resize', function () { window.setTimeout(sync, 300); });
        sync();
      })(slider, flkty);
    }
  }

  // Fix mismatched aria-label="cart" on app-rendered quick add-to-cart buttons (WCAG 2.5.3, 4.1.2)
  function enhanceAddToCartButtons() {
    var addButtons = document.querySelectorAll(
      '.spf-product__form-btn-addtocart, .button--add-to-cart, [data-add-to-cart-trigger], .product__add-to-cart-button'
    );
    for (var i = 0; i < addButtons.length; i++) {
      var btn = addButtons[i];
      var ariaLabel = (btn.getAttribute('aria-label') || '').trim().toLowerCase();
      if (!ariaLabel || ariaLabel === 'cart') {
        var text = (btn.textContent || '').trim() || (btn.getAttribute('data-available') || '').trim() || 'Add to cart';
        text = text.replace(/\s+/g, ' ').trim();
        btn.setAttribute('aria-label', text);
      }
    }
  }

  // Sort and filter dropdowns accessible name (WCAG 4.1.2, 1.3.1)
  function enhanceSortDropdowns() {
    var sortSelects = document.querySelectorAll(
      'select.sort_by, #sort-by, select[name="sort_by"], select.spf-sort, .globo-filter-sort select, .collection__sort-by-filter select'
    );
    for (var i = 0; i < sortSelects.length; i++) {
      var select = sortSelects[i];
      if (!select.getAttribute('aria-label') && !select.getAttribute('aria-labelledby')) {
        var label = select.id ? document.querySelector('label[for="' + select.id + '"]') : null;
        if (!label) {
          select.setAttribute('aria-label', 'Sort by');
        }
      }
    }

    var tagSelects = document.querySelectorAll('select.tag_filter, #tag_filter, select[name="tag_filter"]');
    for (var j = 0; j < tagSelects.length; j++) {
      var tagSelect = tagSelects[j];
      if (!tagSelect.getAttribute('aria-label') && !tagSelect.getAttribute('aria-labelledby')) {
        var tagLabel = tagSelect.id ? document.querySelector('label[for="' + tagSelect.id + '"]') : null;
        if (!tagLabel) {
          tagSelect.setAttribute('aria-label', 'Filter by tag');
        }
      }
    }
  }

  // Product card prices keyboard focusability on innermost spans (WCAG 2.1.1, 4.1.2)
  function enhanceProductPrices() {
    // Remove tabindex and aria-label from wrapper divs so focus goes to inner spans
    var priceWrappers = document.querySelectorAll(
      '.spf-product-card__price-wrapper-custom, .spf-product-card__price-wrapper'
    );
    for (var i = 0; i < priceWrappers.length; i++) {
      priceWrappers[i].removeAttribute('tabindex');
      priceWrappers[i].removeAttribute('aria-label');
    }

    // Set tabindex="0" on innermost price spans
    var priceSpans = document.querySelectorAll(
      '.spf-product-card__price, .spf-product-card__saleprice, .spf-product-card__oldprice, .product-thumbnail__price .money'
    );
    for (var j = 0; j < priceSpans.length; j++) {
      var span = priceSpans[j];
      if (span.getAttribute('tabindex') !== '0') {
        span.setAttribute('tabindex', '0');
      }
    }
  }

  // Product card title accessible announcement (WCAG 2.4.4, 4.1.2)
  function enhanceProductCardTitles() {
    var titleLinks = document.querySelectorAll(
      '.spf-product-card__title a, a.product-thumbnail__title, .product-card__title a, .product__title a'
    );
    for (var i = 0; i < titleLinks.length; i++) {
      var link = titleLinks[i];
      var title = link.textContent.trim();
      if (title) {
        var cleanTitle = title.replace(/^View Details for\s+/i, '');
        var titleLabel = 'View Details for ' + cleanTitle;
        if (link.getAttribute('aria-label') !== titleLabel) {
          link.setAttribute('aria-label', titleLabel);
        }
      }
    }
  }

  // Product size and color swatches keyboard and screen reader accessibility (WCAG 2.1.1, 4.1.2)
  function enhanceProductSwatches() {
    var swatches = document.querySelectorAll(
      '.spf-product__swatch, .swatch__item, .product-thumbnail__swatch'
    );

    for (var i = 0; i < swatches.length; i++) {
      var swatch = swatches[i];
      // Skip +N counter bubbles which are purely numeric badges, not selectable options
      if (
        swatch.querySelector('.spf-product__swatch-color-number') ||
        (swatch.textContent && swatch.textContent.trim().startsWith('+'))
      ) {
        continue;
      }

      if (!swatch.getAttribute('role')) {
        swatch.setAttribute('role', 'button');
      }

      if (!swatch.hasAttribute('tabindex')) {
        swatch.setAttribute('tabindex', '0');
      }

      var isSelected =
        swatch.classList.contains('is-selected') || swatch.classList.contains('active');
      swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      var isSoldOut =
        swatch.classList.contains('soldout') ||
        swatch.classList.contains('sold-out') ||
        swatch.classList.contains('is-sold-out');
      if (isSoldOut) {
        swatch.setAttribute('aria-disabled', 'true');
      } else {
        swatch.removeAttribute('aria-disabled');
      }

      var title =
        swatch.getAttribute('title') ||
        swatch.getAttribute('data-option-value') ||
        swatch.textContent.trim();
      if (title) {
        var label = title.replace(/^(size|color)\s*[:\-]?\s*/i, '').trim();
        var parentWrapper = swatch.closest('.spf-product__swatchs, .swatches, [data-option]');
        var hasColorElement = swatch.querySelector(
          '.spf-product__swatch-color, .spf-product__swatch-image, .swatch-element.color'
        );

        var optionName = '';
        if (parentWrapper) {
          if (
            parentWrapper.classList.contains('Size') ||
            parentWrapper.getAttribute('data-option') === 'Size'
          ) {
            optionName = 'Size';
          } else if (
            parentWrapper.classList.contains('Color') ||
            parentWrapper.classList.contains('collection-color-swt') ||
            parentWrapper.getAttribute('data-option') === 'Color'
          ) {
            optionName = 'Color';
          }
        }

        if (!optionName) {
          optionName = hasColorElement ? 'Color' : 'Size';
        }

        label = optionName + ' ' + label;

        if (isSoldOut) {
          label += ' (Sold out)';
        }
        if (swatch.getAttribute('aria-label') !== label) {
          swatch.setAttribute('aria-label', label);
        }
      }

      if (!swatch.hasAttribute('data-a11y-swatch-ready')) {
        swatch.setAttribute('data-a11y-swatch-ready', 'true');
        swatch.addEventListener('keydown', handleSwatchKeydown);
      }
    }
  }

  function handleSwatchKeydown(e) {
    var isEnter = e.key === 'Enter' || e.keyCode === 13;
    var isSpace = e.key === ' ' || e.key === 'Spacebar' || e.keyCode === 32;
    if (isEnter || isSpace) {
      e.preventDefault();
      e.target.click();
      scheduleScan();
    }
  }

  // Handle Enter and Space keys on search trigger (WCAG 2.1.1)
  function handleSearchTriggerKeydown(event) {
    var trigger =
      event.target.closest && event.target.closest('[data-show-search-trigger]');
    if (!trigger) return;

    var isEnter = event.key === 'Enter' || event.keyCode === 13;
    var isSpace = event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32;

    if (!isEnter && !isSpace) return;

    if (isEnter) {
      if (trigger.tagName === 'BUTTON') {
        event.stopImmediatePropagation();
      } else {
        event.preventDefault();
        event.stopImmediatePropagation();
        openSearchModal(trigger);
      }
    } else if (isSpace) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openSearchModal(trigger);
    }
  }

  function openSearchModal(trigger) {
    if (window.PXUTheme && window.PXUTheme.jsHeader && window.PXUTheme.jsHeader.showSearch) {
      window.PXUTheme.jsHeader.showSearch();
    } else if (trigger) {
      trigger.click();
    }
  }

  function initSearchModalEvents() {
    // Prevent duplicate Fancybox modal instances and restore focus on close
    if (window.PXUTheme && window.PXUTheme.jsHeader) {
      if (window.PXUTheme.jsHeader.showSearch) {
        var origShowSearch = window.PXUTheme.jsHeader.showSearch;
        var searchOpening = false;
        window.PXUTheme.jsHeader.showSearch = function () {
          if (searchOpening || (window.jQuery && window.jQuery.fancybox && window.jQuery.fancybox.getInstance())) {
            return;
          }
          searchOpening = true;
          setTimeout(function () {
            searchOpening = false;
          }, 500);
          return origShowSearch.apply(this, arguments);
        };
      }

      if (window.PXUTheme.jsHeader.hideSearch) {
        var origHideSearch = window.PXUTheme.jsHeader.hideSearch;
        window.PXUTheme.jsHeader.hideSearch = function () {
          var overlay = document.querySelector('[data-search-type="overlay"]');
          var wasOpen = overlay && overlay.classList.contains('is-opened');
          var res = origHideSearch.apply(this, arguments);
          if (wasOpen) {
            var trigger = document.querySelector('[data-show-search-trigger]');
            if (trigger) {
              trigger.focus();
            }
          }
          return res;
        };
      }

    }

    if (window.jQuery) {
      window.jQuery(document).on('afterShow.fb onInit.fb beforeShow.fb', function (e, instance) {
        if (instance && instance.$refs && instance.$refs.container) {
          instance.$refs.container
            .find('[data-fancybox-close], .fancybox-close-small, .fancybox-button--close, button.fancybox-button')
            .attr({
              'aria-label': 'Close',
              'title': 'Close'
            });
        }
      });
      window.jQuery(document).on('afterClose.fb', function (e, instance) {
        if (instance && instance.$refs && instance.$refs.container && instance.$refs.container.hasClass('search__lightbox')) {
          var trigger = document.querySelector('[data-show-search-trigger]');
          if (trigger) {
            trigger.focus();
          }
        }
      });
    }
  }

  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    window.setTimeout(scan, 200);
  }

  function init() {
    scan();
    initSearchModalEvents();

    document.addEventListener('keydown', handleActivation, true);
    document.addEventListener('keydown', handleEscape, true);
    document.addEventListener('keydown', handleSearchTriggerKeydown, true);

    /* The theme toggles menus from several places (hover, click, its own
       keyboard handler) — re-read the state after anything that could change
       it rather than trying to intercept each one. */
    ['focusin', 'focusout', 'click', 'change', 'keyup'].forEach(function (type) {
      document.addEventListener(type, scheduleSync, true);
    });

    /* Mega menus are cloned into the header and the mobile menu after load,
       the theme editor re-renders sections, and 3rd-party accessibility tools
       (like UserWay) can alter attributes dynamically. */
    new MutationObserver(function (mutations) {
      var needsImmediateFix = false;
      for (var m = 0; m < mutations.length; m++) {
        var mut = mutations[m];
        if (mut.type === 'attributes' && mut.attributeName === 'aria-label') {
          var target = mut.target;
          if (
            target &&
            target.matches &&
            target.matches('.spf-product__form-btn-addtocart, .button--add-to-cart, [data-add-to-cart-trigger]')
          ) {
            var current = (target.getAttribute('aria-label') || '').trim().toLowerCase();
            if (current === 'cart') {
              var text = (target.textContent || '').trim() || (target.getAttribute('data-available') || '').trim() || 'Add to cart';
              text = text.replace(/\s+/g, ' ').trim();
              target.setAttribute('aria-label', text);
              needsImmediateFix = true;
            }
          }
        }
      }
      scheduleScan();
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label', 'class', 'data-uw-rm-empty-ctrl']
    });

    document.addEventListener('shopify:section:load', scheduleScan);
    window.addEventListener('load', scheduleScan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
